import { createFileRoute, Link } from "@tanstack/react-router";
import { motion } from "framer-motion";
import { Check, Sparkles, Zap, Crown, ArrowRight, Loader2, FileText, ShieldCheck } from "lucide-react";
import { useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { CookieBanner } from "@/components/CookieBanner";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { buildSeoMeta } from "@/lib/seo";
import { createCheckoutSession } from "@/lib/checkout.functions";

export const Route = createFileRoute("/cennik")({
  head: () =>
    buildSeoMeta({
      title: "Cennik wdrożeń e-commerce — Baselinker, SellAsist, Shopify | SyncTech",
      description:
        "Wdrożenie kompleksowe 3 999 zł, abonament wsparcia 799 zł/mies., wycena indywidualna w kalkulatorze. Bez VAT (zwolnienie podmiotowe).",
      path: "/cennik",
    }),
  component: CennikPage,
});

type Pkg = {
  name: string;
  icon: typeof Zap;
  oldPrice?: string;
  price: string;
  priceSuffix?: string;
  discount?: string;
  tagline: string;
  description: string;
  features: string[];
  cta: string;
  ctaType: "stripe" | "calculator";
  stripePkg?: "wdrozenie" | "wsparcie";
  highlighted: boolean;
  badge?: string;
  accent: "teal" | "amber" | "violet";
};

const packages: Pkg[] = [
  {
    name: "Abonament wsparcia",
    icon: Zap,
    oldPrice: "1 199 zł",
    price: "799 zł",
    priceSuffix: "/ mies.",
    discount: "-33%",
    stripePkg: "wsparcie",
    tagline: "Spokój i ciągłość",
    description:
      "5 godzin pomocy technicznej miesięcznie — dorzucisz integrację, naprawimy błąd, podkręcimy automatyzację. Bez stresu, bez czekania.",
    features: [
      "5 h pracy w miesiącu (rozliczanie 15-min)",
      "Priorytetowy kontakt e-mail",
      "Czas reakcji do 24h w dni robocze",
      "Drobne integracje i poprawki",
      "Optymalizacja istniejących automatyzacji",
      "Konsultacje i doradztwo",
      "Miesięczny raport wykonanych prac",
      "Niewykorzystane godziny przechodzą +1 mies.",
    ],
    cta: "Subskrybuj",
    ctaType: "stripe",
    highlighted: false,
    accent: "amber",
  },
  {
    name: "Wdrożenie kompleksowe",
    icon: Sparkles,
    oldPrice: "5 999 zł",
    price: "3 999 zł",
    discount: "-33%",
    stripePkg: "wdrozenie",
    tagline: "Najczęściej wybierany",
    description:
      "Kompletny pakiet startowy: platforma sprzedażowa skonfigurowana od A do Z — gotowa do przyjmowania zamówień jeszcze w tym tygodniu.",
    features: [
      "Wdrożenie Baselinker lub SellAsist",
      "Akcje automatyczne (workflow)",
      "Konfiguracja statusów zamówień",
      "Powiadomienia e-mail (transakcyjne + marketingowe)",
      "Integracja bramek płatności",
      "Drukarki etykiet (zebra / termiczne)",
      "Integracje kurierskie (InPost, DPD, DHL, GLS)",
      "Automatyczne wystawianie faktur",
      "Konta użytkowników i uprawnienia",
      "Szkolenie 1h online (z nagraniem)",
    ],
    cta: "Kup teraz",
    ctaType: "stripe",
    highlighted: true,
    badge: "Promocja -33%",
    accent: "amber",
  },
  {
    name: "Wycena indywidualna",
    icon: Crown,
    price: "konfigurator",
    tagline: "Twój zakres, Twoja cena",
    description:
      "Masz nietypowy projekt — multistore, ERP, B2B, własne API? Złóż konfigurację w kalkulatorze i otrzymaj wycenę na e-mail w 5 minut.",
    features: [
      "Wybierz platformę i skalę sklepu",
      "Zaznacz potrzebne integracje",
      "Cena przeliczana na żywo",
      "Otrzymujesz szczegółową rozpiskę PDF",
      "Bez rejestracji — tylko e-mail",
      "Bezpłatna konsultacja w pakiecie",
    ],
    cta: "Otwórz kalkulator",
    ctaType: "calculator",
    highlighted: false,
    accent: "amber",
  },
];

const faqShort = [
  {
    q: "Jak wygląda płatność?",
    a: "Płatność online przez Stripe (BLIK, karty, Apple/Google Pay) — po kliknięciu pakietu przekierowuję na bezpieczną stronę płatności. Fakturę otrzymujesz mailem zaraz po opłaceniu.",
  },
  {
    q: "Co jeśli pakiet nie pasuje do mojego sklepu?",
    a: "Użyj kalkulatora w trzecim pakiecie — w 2 minuty dostaniesz indywidualną wycenę i szczegółową rozpiskę zakresu prac.",
  },
  {
    q: "Ile trwa wdrożenie kompleksowe?",
    a: "Standardowo 1–2 tygodnie od momentu otrzymania dostępów. Szkolenie online umawiamy na koniec wdrożenia, dostajesz też nagranie.",
  },
  {
    q: "Czy mogę zrezygnować z abonamentu?",
    a: "Tak, w każdej chwili — bez okresu wypowiedzenia. Niewykorzystane godziny z bieżącego miesiąca przechodzą na kolejny.",
  },
];

const faqNav = [
  { id: "faq-pytania", label: "Najczęstsze pytania" },
  { id: "faq-vat", label: "VAT — wyjaśnienie" },
];

const getHeaderOffset = () => {
  const header = document.querySelector("header");
  const h = header?.getBoundingClientRect().height ?? 80;
  return h + 16;
};

const scrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
  window.scrollTo({ top, behavior: "smooth" });
};

// CTA pakietowe prowadzą do sekcji kontaktu (płatności online — wkrótce).

function CennikPage() {
  const checkout = useServerFn(createCheckoutSession);
  const [loadingPkg, setLoadingPkg] = useState<"wdrozenie" | "wsparcie" | null>(null);
  const [activeFaq, setActiveFaq] = useState<string>("faq-pytania");
  const [selectedPkg, setSelectedPkg] = useState<string>(
    packages.find((p) => p.highlighted)?.name ?? packages[0].name,
  );

  useEffect(() => {
    const ids = faqNav.map((n) => n.id);
    const els = ids
      .map((id) => document.getElementById(id))
      .filter((el): el is HTMLElement => !!el);
    if (els.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        if (visible[0]) {
          setActiveFaq(visible[0].target.id);
        }
      },
      { rootMargin: `-${getHeaderOffset()}px 0px -55% 0px`, threshold: 0 },
    );

    els.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  const handleCheckout = async (pkg: "wdrozenie" | "wsparcie") => {
    try {
      setLoadingPkg(pkg);
      const { url } = await checkout({ data: { pkg } });
      if (url) window.location.href = url;
      else throw new Error("Brak URL sesji płatności");
    } catch (e) {
      console.error(e);
      toast.error("Nie udało się rozpocząć płatności. Spróbuj ponownie.");
      setLoadingPkg(null);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <main className="pt-32 pb-24">
        <section className="mx-auto max-w-5xl px-6 text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-display text-4xl font-bold md:text-6xl"
          >
            Trzy proste pakiety — <span className="text-gradient">bez ukrytych kosztów</span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground"
          >
            Wdrożenie kompleksowe, miesięczny abonament wsparcia albo indywidualny kalkulator — wybierz to, co pasuje do Twojego sklepu.
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            className="mx-auto mt-3 max-w-xl text-xs text-muted-foreground"
          >
            Wszystkie ceny są kwotami do zapłaty — Usługodawca korzysta ze zwolnienia z VAT (art. 113 ust. 1 i 9 ustawy o VAT).
          </motion.p>
        </section>

        <section className="mx-auto mt-16 max-w-7xl px-6 lg:pt-10 lg:pb-6">
          <div className="grid items-stretch gap-8 overflow-visible lg:grid-cols-3">
            {packages.map((pkg, i) => {
              const Icon = pkg.icon;
              const accentStyle = {
                ["--pkg" as string]: `var(--pkg-${pkg.accent})`,
                ["--pkg-glow" as string]: `var(--pkg-${pkg.accent}-glow)`,
              } as React.CSSProperties;
              const isSelected = selectedPkg === pkg.name;
              return (
                <motion.div
                  key={pkg.name}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  onClick={() => setSelectedPkg(pkg.name)}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setSelectedPkg(pkg.name);
                    }
                  }}
                  aria-pressed={isSelected}
                  style={{
                    ...accentStyle,
                    borderColor: isSelected
                      ? "color-mix(in oklab, var(--pkg) 65%, transparent)"
                      : "color-mix(in oklab, var(--pkg) 35%, transparent)",
                    background: isSelected
                      ? "linear-gradient(160deg, color-mix(in oklab, var(--pkg) 18%, var(--card)) 0%, color-mix(in oklab, var(--pkg-glow) 8%, var(--card)) 100%)"
                      : "linear-gradient(160deg, color-mix(in oklab, var(--pkg) 10%, var(--card)) 0%, color-mix(in oklab, var(--pkg-glow) 4%, var(--card)) 100%)",
                    boxShadow: isSelected
                      ? "0 20px 50px -20px color-mix(in oklab, var(--pkg) 45%, transparent)"
                      : "0 12px 32px -18px color-mix(in oklab, var(--pkg) 35%, transparent)",
                  }}
                  className={`group relative flex cursor-pointer flex-col overflow-hidden rounded-3xl border p-8 ring-2 ring-transparent transition-all duration-500 ease-out hover:-translate-y-2 hover:border-[color:color-mix(in_oklab,var(--pkg)_85%,transparent)] hover:ring-[color:color-mix(in_oklab,var(--pkg)_55%,transparent)] hover:ring-offset-2 hover:ring-offset-background focus-visible:outline-none focus-visible:ring-[color:color-mix(in_oklab,var(--pkg)_70%,transparent)] focus-visible:ring-offset-2 focus-visible:ring-offset-background ${isSelected ? "lg:z-10 lg:scale-[1.06] lg:-translate-y-4" : ""}`}
                >
                  {/* Subtelna aura w kolorze akcentu — pojawia się na hover */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute -inset-px opacity-0 transition-opacity duration-500 ease-out group-hover:opacity-100"
                    style={{
                      background:
                        "radial-gradient(60% 50% at 50% 0%, color-mix(in oklab, var(--pkg-glow) 28%, transparent) 0%, transparent 70%)",
                    }}
                  />
                  {/* Smuga światła przesuwająca się po krawędzi na hover */}
                  <div
                    aria-hidden
                    className="pointer-events-none absolute inset-0 rounded-3xl opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                    style={{
                      boxShadow:
                        "inset 0 1px 0 color-mix(in oklab, var(--pkg-glow) 55%, transparent), 0 30px 70px -25px color-mix(in oklab, var(--pkg) 55%, transparent)",
                    }}
                  />
                  {pkg.badge && (
                    <div
                      className="absolute right-6 top-6 rounded-full px-3 py-1 text-xs font-bold text-primary-foreground transition-transform duration-500 ease-out group-hover:-translate-y-0.5"
                      style={{
                        background: "linear-gradient(90deg, var(--pkg), var(--pkg-glow))",
                        boxShadow: "0 0 30px color-mix(in oklab, var(--pkg-glow) 55%, transparent)",
                      }}
                    >
                      {pkg.badge}
                    </div>
                  )}
                  <div
                    className="relative mb-5 inline-flex h-12 w-12 items-center justify-center rounded-2xl text-primary-foreground transition-transform duration-500 ease-out group-hover:-translate-y-1 group-hover:scale-105 group-hover:rotate-[-3deg]"
                    style={{
                      background: "linear-gradient(135deg, var(--pkg), var(--pkg-glow))",
                      boxShadow: "0 0 30px color-mix(in oklab, var(--pkg-glow) 50%, transparent)",
                    }}
                  >
                    <Icon className="h-6 w-6" />
                  </div>
                  <h2 className="font-display text-2xl font-bold">{pkg.name}</h2>
                  <p className="mt-1 text-sm text-muted-foreground">{pkg.tagline}</p>

                  <div className="mt-5 flex flex-wrap items-end gap-x-3 gap-y-1">
                    {pkg.oldPrice && (
                      <span className="font-display text-2xl font-semibold text-muted-foreground/70 line-through decoration-2 decoration-destructive/70">
                        {pkg.oldPrice}
                      </span>
                    )}
                    <div className="font-display text-4xl font-bold text-foreground">
                      {pkg.price}
                      {pkg.priceSuffix && (
                        <span className="ml-1 text-base font-medium text-muted-foreground">
                          {pkg.priceSuffix}
                        </span>
                      )}
                    </div>
                    {pkg.discount && (
                      <span className="rounded-full bg-destructive/15 px-2 py-0.5 text-xs font-bold uppercase tracking-wide text-destructive">
                        {pkg.discount}
                      </span>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-muted-foreground">
                    bez VAT (zwolnienie podmiotowe) {pkg.oldPrice && <span className="text-primary">• cena promocyjna</span>}
                  </p>

                  <p className="mt-4 text-sm text-muted-foreground">{pkg.description}</p>

                  <ul className="mt-6 space-y-2.5">
                    {pkg.features.map((f) => (
                      <li key={f} className="flex items-start gap-2.5 text-sm">
                        <Check className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-auto pt-8">
                    {pkg.ctaType === "calculator" ? (
                      <Button
                        asChild
                        size="lg"
                        variant="outline"
                        style={{
                          borderColor: "color-mix(in oklab, var(--primary) 60%, transparent)",
                          color: "var(--primary)",
                        }}
                        className="group/btn w-full bg-transparent font-semibold transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-primary/5 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-0 active:scale-[0.98]"
                      >
                        <Link to="/kalkulator">
                          {pkg.cta} <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" />
                        </Link>
                      </Button>
                    ) : (
                      <Button
                        size="lg"
                        disabled={loadingPkg === pkg.stripePkg}
                        onClick={() => pkg.stripePkg && handleCheckout(pkg.stripePkg)}
                        style={{
                          background: "linear-gradient(90deg, var(--primary), var(--primary-glow))",
                          boxShadow: "0 12px 32px -10px color-mix(in oklab, var(--primary) 60%, transparent)",
                        }}
                        className="group/btn w-full font-semibold text-primary-foreground transition-all duration-300 ease-out hover:-translate-y-0.5 hover:brightness-110 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-0 active:scale-[0.98]"
                      >
                        {loadingPkg === pkg.stripePkg ? (
                          <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Przekierowuję…</>
                        ) : (
                          <>{pkg.cta} <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" /></>
                        )}
                      </Button>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </section>

        <section className="mx-auto mt-20 max-w-3xl px-6">
          <div className="mb-8 flex flex-wrap items-center justify-center gap-2">
            {faqNav.map((n) => {
              const isActive = activeFaq === n.id;
              return (
                <Button
                  key={n.id}
                  size="sm"
                  onClick={() => scrollToId(n.id)}
                  aria-current={isActive ? "true" : undefined}
                  className={`rounded-full border font-semibold transition-all duration-300 ease-out focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.96] ${
                    isActive
                      ? "border-transparent bg-gradient-to-r from-primary to-primary-glow text-primary-foreground shadow-[var(--shadow-glow)] hover:-translate-y-0.5 hover:brightness-110"
                      : "border-primary/40 bg-transparent text-primary hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.4)]"
                  }`}
                >
                  {n.label}
                </Button>
              );
            })}
          </div>
          <h2 id="faq-pytania" className="scroll-mt-24 text-center font-display text-3xl font-bold md:text-4xl">
            Najczęstsze pytania
          </h2>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5 }}
            className="mt-10 rounded-3xl border border-border bg-card/60 p-2 shadow-elegant sm:p-4"
          >
            <Accordion type="single" collapsible className="w-full">
              {faqShort.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-border/50">
                  <AccordionTrigger className="px-4 text-left font-semibold transition-colors hover:no-underline hover:text-primary data-[state=open]:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </section>

        <section className="mx-auto mt-20 max-w-3xl px-6">
          <div className="mb-6 flex items-center justify-center gap-2 text-primary">
            <ShieldCheck className="h-5 w-5" />
            <span className="text-xs font-semibold uppercase tracking-widest">
              Zwolnienie z VAT
            </span>
          </div>
          <h2 id="faq-vat" className="scroll-mt-24 text-center font-display text-3xl font-bold md:text-4xl">
            VAT — krótkie wyjaśnienie
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm text-muted-foreground">
            Korzystam ze zwolnienia podmiotowego (art. 113 ust. 1 i 9 ustawy o VAT) — poniżej najważniejsze odpowiedzi.
          </p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="mt-10 rounded-3xl border border-border bg-card/60 p-2 shadow-elegant sm:p-4"
          >
            <Accordion type="single" collapsible className="w-full">
              {[
                {
                  q: "Dlaczego nie ma VAT na fakturze?",
                  a: "Korzystam ze zwolnienia podmiotowego ze względu na nieprzekroczenie limitu obrotu (art. 113 ust. 1 i 9 ustawy o VAT). Kwota brutto = kwota netto = kwota do zapłaty.",
                },
                {
                  q: "Czy mogę odliczyć ten koszt?",
                  a: "Tak. Faktura bez VAT jest pełnoprawnym dokumentem księgowym — ujmujesz ją w kosztach uzyskania przychodu jak każdą inną fakturę.",
                },
                {
                  q: "Czy cena się zmieni, jeśli przekroczę limit?",
                  a: "Jeśli w trakcie roku stracę prawo do zwolnienia, doliczę 23% VAT do nowych zleceń. Faktury wystawione wcześniej pozostają bez zmian.",
                },
                {
                  q: "Jak rozliczam podatek dochodowy?",
                  a: "Ryczałtem ewidencjonowanym 12% — to wewnętrzna kwestia mojej działalności i nie wpływa na cenę dla Ciebie.",
                },
              ].map((f, i) => (
                <AccordionItem key={f.q} value={`vat-${i}`} className="border-border/50">
                  <AccordionTrigger className="px-4 text-left font-semibold transition-colors hover:no-underline hover:text-primary data-[state=open]:text-primary">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="px-4 text-sm leading-relaxed text-muted-foreground">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>

          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              variant="outline"
              size="sm"
              className="group/btn rounded-full border-primary/40 bg-transparent px-4 font-semibold text-primary transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:text-primary hover:shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.4)] focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97]"
            >
              <Link to="/regulamin">
                <FileText className="mr-2 h-4 w-4 transition-transform duration-300 group-hover/btn:scale-110" /> Regulamin
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="group/btn rounded-full border-primary/40 bg-transparent px-4 font-semibold text-primary transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:text-primary hover:shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.4)] focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97]"
            >
              <Link to="/polityka-prywatnosci">
                <FileText className="mr-2 h-4 w-4 transition-transform duration-300 group-hover/btn:scale-110" /> Polityka prywatności
              </Link>
            </Button>
            <Button
              asChild
              variant="outline"
              size="sm"
              className="group/btn rounded-full border-primary/40 bg-transparent px-4 font-semibold text-primary transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:text-primary hover:shadow-[0_6px_18px_-6px_hsl(var(--primary)/0.4)] focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:scale-[0.97]"
            >
              <a
                href="https://isap.sejm.gov.pl/isap.nsf/DocDetails.xsp?id=WDU20040540535"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FileText className="mr-2 h-4 w-4 transition-transform duration-300 group-hover/btn:scale-110" /> Ustawa o VAT (ISAP)
              </a>
            </Button>
          </div>
        </section>

        <section className="mx-auto mt-20 max-w-4xl px-6">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-primary/20 via-primary/10 to-transparent p-10 text-center backdrop-blur-md md:p-14">
            <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full bg-primary/30 blur-3xl" />
            <div className="relative">
              <h2 className="font-display text-2xl font-bold md:text-3xl">
                Nie wiesz, co wybrać?
              </h2>
              <p className="mx-auto mt-3 max-w-xl text-muted-foreground">
                Umów bezpłatną 30-min rozmowę — dopasujemy pakiet do Twojego sklepu.
              </p>
              <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
                <Button
                  asChild
                  size="lg"
                  className="group/btn bg-gradient-to-r from-primary to-primary-glow bg-[length:200%_100%] bg-left font-semibold text-primary-foreground shadow-[var(--shadow-glow)] transition-all duration-300 ease-out hover:-translate-y-0.5 hover:bg-right hover:shadow-[0_12px_32px_-8px_hsl(var(--primary)/0.6)] hover:brightness-110 focus-visible:ring-2 focus-visible:ring-primary/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-0 active:scale-[0.98] active:brightness-95"
                >
                  <a href="/#umow-rozmowe">Umów rozmowę <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-300 group-hover/btn:translate-x-1" /></a>
                </Button>
                <Button
                  asChild
                  size="lg"
                  variant="outline"
                  className="group/btn border-primary/60 bg-transparent font-semibold text-primary transition-all duration-300 ease-out hover:-translate-y-0.5 hover:border-primary hover:bg-primary/10 hover:text-primary hover:shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.45)] focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-background active:translate-y-0 active:scale-[0.98]"
                >
                  <Link to="/kalkulator">Otwórz kalkulator</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
      <CookieBanner />
      <Toaster richColors position="top-right" />
    </div>
  );
}
